import { createPopper } from "@popperjs/core";

import ApplicationController from "../application_component_controller";
import URL from "hb/lib/url";
import { useResize } from "hb/insights/use_resize";
import { useOutlets } from "hb/insights/use_outlets";
import { useListen } from "hb/insights/use_listen";
import { useClickOutside } from "hb/insights/use_click_outside";

export default class extends ApplicationController {
  static targets = [
    "list",
    "more",
    "container",
    "dropdown",
    "toggleItem",
    "toggleGroup",
    "selectedItem",
    "hiddenInput",
    "missingStreams",
    "hiddenInputTemplate",
  ];

  static values = {
    updateParams: Boolean,
    showSubject: Boolean,
    readonly: Boolean,
  };

  connect() {
    useOutlets(this);
    useListen(this);
    this.popper = createPopper(this.element, this.dropdownTarget, {
      placement: "bottom-start",
    });
    this.updateMoreText();
    this.showingAll = false;
    useResize(this, { element: this.containerTarget });
    useClickOutside(this);

    this.streamIdInputTemplate = this.compileElement(
      this.hiddenInputTemplateTarget,
    );
  }

  clickedOutside() {
    if (this.showingAll) {
      this.toggleShowAll();
    } else {
      this.closeDropdown();
    }
  }

  get selectedStreamIds() {
    return Array.from(this.findAll(".toggle-item.selected")).map(
      (i) => i.dataset.id,
    );
  }

  toggleItem(id) {
    const selected = this.find(`.selected-item[data-id="${id}"]`);
    selected.classList.toggle("hidden");
    const toggle = this.find(`.toggle-item[data-id="${id}"]`);
    toggle.classList.toggle("selected");
    toggle.children[0].checked = !toggle.children[0].checked;

    const streamIds = this.selectedStreamIds;

    this.updateMoreText();

    if (this.updateParamsValue) {
      URL.updateParam("stream_ids", streamIds);
    }

    this.emitWindow("queryStreamChanged", { streamIds });

    this.hiddenInputTargets.forEach((el) => el.remove());
    streamIds.forEach((id) => {
      this.element.insertAdjacentHTML(
        "beforeend",
        this.streamIdInputTemplate({ id }),
      );
    });

    if (streamIds.length === 0) {
      this.missingStreamsTarget.classList.remove("hidden");
    } else {
      this.missingStreamsTarget.classList.add("hidden");
    }
    window.dispatchEvent(new Event("streamSelected"));
  }

  toggleSelected({ target }) {
    this.toggleItem(target.dataset.id);
  }

  removeSelected(evt) {
    this.toggleItem(evt.target.parentElement.dataset.id);

    evt.preventDefault();
    evt.stopPropagation();
  }

  resize() {
    this.updateMoreText();
    if (this.dropdownTarget.classList.contains("visible")) {
      this.fitDropdown();
    }
  }

  updateMoreText() {
    const containerWidth = this.containerTarget.clientWidth;
    const itemsWidth = this.listTarget.scrollWidth;
    let hiddenItems = 0;

    if (itemsWidth > containerWidth) {
      let width = 0;

      Array.from(this.selectedItemTargets).forEach((item) => {
        if (item.classList.contains("hidden")) return;
        width += item.offsetWidth + 3;
        if (width > containerWidth) {
          hiddenItems++;
        }
      });
    }

    if (hiddenItems > 0) {
      this.moreTarget.textContent = `${hiddenItems} more...`;
      this.moreTarget.style.display = "inline-block";
    } else {
      this.moreTarget.style.display = "none";
    }
  }

  toggleShowAll() {
    this.showingAll = !this.showingAll;
    this.closeDropdown();
    this.element.classList.toggle("show-all");
  }

  closeShowAll() {
    this.showingAll = false;
    this.element.classList.remove("show-all");
  }

  isWordStartMatch(sentence, word) {
    const regex = new RegExp(`\\b${word}`, "i");
    return regex.test(sentence);
  }

  filter(evt) {
    this.toggleItemTargets.forEach((i) => {
      i.classList.remove("hidden");
    });
    const valStr = evt.target.value.toLowerCase();
    if (valStr !== "") {
      this.toggleItemTargets.forEach((item) => {
        if (!this.isWordStartMatch(item.dataset.tokens, valStr)) {
          item.classList.add("hidden");
        }
      });
    }

    if (this.showSubjectValue) {
      this.toggleGroupTargets.forEach((group) => {
        const visibleItems = group.querySelectorAll(
          ".toggle-item:not(.hidden)",
        );
        if (visibleItems.length === 0) {
          group.classList.add("hidden");
        } else {
          group.classList.remove("hidden");
        }
      });
    }
  }

  fitDropdown() {
    const { top } = this.dropdownTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - top;
    this.dropdownTarget.style.maxHeight = `${spaceBelow - 20}px`;
  }

  toggle() {
    if (this.readonlyValue) return;

    this.closeShowAll();
    this.dropdownTarget.classList.toggle("visible");
    this.fitDropdown();
  }

  escaped() {
    if (this.showingAll) {
      this.toggleShowAll();
    } else {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    this.dropdownTarget.classList.remove("visible");
  }
}
